import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Table from "react-bootstrap/Table";
import moment from "moment";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import axios from ".././axios-interceptor";
import { decrypt } from "../utils";
import {
  base_url,
  AUDITOR_ROLE,
  WITHDRAWAL_STATUS_REQUESTED,
  WITHDRAWAL_STATUS_REJECTED,
  WITHDRAWAL_WINDOW_DAYS,
  formatMoney,
} from "../utilities";

// Fund Now (multi-investor) investments. The backend's get_by_investor already derives `state`,
// `can_request_withdrawal` and `withdrawal_window_deadline` — this component only displays them.
// The server re-checks eligibility on every request, so the countdown here is informational.

const STATE_STYLES = {
  funding: { label: "Funding", color: "var(--ben-blue)", background: "#EEF2FF" },
  fully_funded: { label: "Fully funded", color: "var(--ben-navy)", background: "#F4EFE9" },
  withdrawal_requested: { label: "Withdrawal pending", color: "#92400E", background: "#FFF8E8" },
  active: { label: "Active", color: "var(--ben-navy)", background: "#E6EBF8" },
  closed: { label: "Closed", color: "#7C8495", background: "#F2F3F3" },
  withdrawn: { label: "Withdrawn", color: "#ED5151", background: "#FEF2F2" },
  delisted: { label: "Delisted", color: "#6B7280", background: "#F3F4F6" },
};

const pillStyle = (state) => {
  const s = STATE_STYLES[state] || STATE_STYLES.funding;
  return {
    background: s.background,
    color: s.color,
    borderRadius: 12,
    fontSize: 12,
    padding: "2px 10px",
    fontWeight: 600,
    whiteSpace: "nowrap",
    display: "inline-block",
  };
};

export const FundNowStateBadge = ({ state }) => (
  <span style={pillStyle(state)}>{(STATE_STYLES[state] || { label: state || "-" }).label}</span>
);

const timeLeft = (deadline, now) => {
  const ms = Number(deadline) - now;
  if (!deadline || isNaN(ms) || ms <= 0) return "";
  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h left`;
  if (hours > 0) return `${hours}h ${minutes}m left`;
  return `${Math.max(minutes, 1)}m left`;
};

const readRoleCode = () => {
  const raw = Cookies.get("role_code");
  if (!raw) return null;
  const value = decrypt(raw);
  return value === null || value === undefined ? null : Number(value);
};

const labelStyle = { fontSize: 11, color: "#7C8495", marginBottom: 2 };

// variant: "table" (own table), "rows" (bare <tr>s in the My Investments column layout),
// "items" (render prop: sortable items for TableData's extraItems), or compact (dashboard card).
const FundNowInvestments = ({ investorId, compact = false, variant = "table", onLoaded, children }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [isAuditor] = useState(() => readRoleCode() === AUDITOR_ROLE);

  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState("");
  const [eligibility, setEligibility] = useState(null);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;

  const fetchRows = useCallback(() => {
    if (!investorId) return;
    setLoading(true);
    axios
      .post(base_url + "loan-investor-subscriptions/get_by_investor", { investor_id: investorId })
      .then((res) => {
        const data = res?.data?.status === 200 && Array.isArray(res?.data?.data) ? res.data.data : [];
        setRows(data);
        setLoading(false);
        if (onLoadedRef.current) onLoadedRef.current(data);
      })
      .catch(() => {
        setRows([]);
        setLoading(false);
        if (onLoadedRef.current) onLoadedRef.current([]);
      });
  }, [investorId]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  const checkEligibility = (subscription_id) =>
    axios
      .post(base_url + "loan-investor-subscriptions/get_withdrawal_eligibility", { subscription_id })
      .then((res) => (res?.data?.status === 200 ? res.data.data : null))
      .catch(() => null);

  const openWithdrawal = (row) => {
    if (isAuditor) return;
    setSelected(row);
    setReason("");
    setEligibility(null);
    setChecking(true);
    checkEligibility(row.subscription_id).then((result) => {
      setEligibility(result);
      setChecking(false);
    });
  };

  const closeWithdrawal = () => {
    if (submitting) return;
    setSelected(null);
    setEligibility(null);
    setReason("");
  };

  const submitWithdrawal = async () => {
    if (!selected || submitting || isAuditor) return;
    setSubmitting(true);
    // Re-check right before submitting: the window or the funding status may have changed.
    const latest = await checkEligibility(selected.subscription_id);
    if (!latest) {
      setSubmitting(false);
      return;
    }
    if (!latest.eligible) {
      setEligibility(latest);
      setSubmitting(false);
      return;
    }
    const res = await axios
      .post(base_url + "loan-investor-subscriptions/request_withdrawal", {
        subscription_id: selected.subscription_id,
        withdrawal_reason: reason.trim(),
      })
      .catch(() => null);
    if (res?.data?.status === 200) {
      toast.success(res.data.message || "Withdrawal request submitted", { toastId: 320 });
      setSubmitting(false);
      setSelected(null);
      setEligibility(null);
      setReason("");
      fetchRows();
    } else {
      // The interceptor has already shown the backend's message.
      setSubmitting(false);
    }
  };

  const renderWithdrawalCell = (row) => {
    const pending =
      row.withdrawal_status === WITHDRAWAL_STATUS_REQUESTED || row.state === "withdrawal_requested";
    const rejected = row.withdrawal_status === WITHDRAWAL_STATUS_REJECTED;
    return (
      <div style={{ minWidth: 180, maxWidth: 240 }}>
        {pending && (
          <div style={{ fontSize: 12, color: "#92400E", fontWeight: 600 }}>
            Withdrawal requested — awaiting admin review
          </div>
        )}
        {rejected && !pending && (
          <div style={{ fontSize: 12, marginBottom: row.can_request_withdrawal ? 6 : 0 }}>
            <div style={{ color: "#ED5151", fontWeight: 600 }}>Withdrawal request rejected</div>
            {row.withdrawal_review_notes ? (
              // QA #37: one line, full note on hover, so a long note can't stretch the column.
              <div
                title={row.withdrawal_review_notes}
                style={{ color: "#7C8495", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "help" }}
              >
                Admin note: {row.withdrawal_review_notes}
              </div>
            ) : null}
          </div>
        )}
        {row.can_request_withdrawal && !isAuditor && (
          <div className="d-flex flex-column align-items-start gap-1">
            <button
              type="button"
              className="btn blue_btn"
              style={{ fontSize: 12, padding: "4px 12px" }}
              disabled={submitting}
              onClick={() => openWithdrawal(row)}
            >
              Request withdrawal
            </button>
            <span style={{ fontSize: 11, color: "var(--ben-blue)", fontWeight: 600 }}>
              {timeLeft(row.withdrawal_window_deadline, now) || "Window closing"}
            </span>
          </div>
        )}
        {!pending && !rejected && !row.can_request_withdrawal && <span style={{ color: "#7C8495" }}>—</span>}
      </div>
    );
  };

  const activeRows = rows.filter((r) => r.state !== "withdrawn");
  const totalsByCurrency = activeRows.reduce((acc, r) => {
    const c = r.currency || "BHD";
    acc[c] = acc[c] || { invested: 0, received: 0, profit: 0 };
    acc[c].invested += Number(r.invested_amount || 0);
    acc[c].received += Number(r.received_total || 0);
    acc[c].profit += Number(r.profit_received || 0);
    return acc;
  }, {});
  const pendingCount = rows.filter((r) => r.withdrawal_status === WITHDRAWAL_STATUS_REQUESTED).length;

  if (compact) {
    if (!loading && rows.length === 0) return null;
    return (
      <div className="card shadow-ben mb-3 p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="Inv_db_card_tilte mb-0">Fund Now investments</h4>
          <Link to="/myinvestments" style={{ fontSize: 13, fontWeight: 600, color: "#3D79F3" }}>
            View all
          </Link>
        </div>
        {loading ? (
          <Skeleton height={40} count={2} />
        ) : (
          <>
            <div className="d-flex flex-wrap gap-5 mb-3">
              <div>
                <div style={labelStyle}>Investments</div>
                <div style={{ color: "#3D79F3", fontSize: 21, fontWeight: 500 }}>{activeRows.length}</div>
              </div>
              {Object.entries(totalsByCurrency).map(([currency, t]) => (
                <React.Fragment key={currency}>
                  <div>
                    <div style={labelStyle}>Invested ({currency})</div>
                    <div style={{ color: "#3D79F3", fontSize: 21, fontWeight: 500 }}>{formatMoney(t.invested, currency)}</div>
                  </div>
                  <div>
                    <div style={labelStyle}>Received ({currency})</div>
                    <div style={{ color: "#BAA38B", fontSize: 21, fontWeight: 500 }}>{formatMoney(t.received, currency)}</div>
                  </div>
                </React.Fragment>
              ))}
              {pendingCount > 0 && (
                <div>
                  <div style={labelStyle}>Withdrawals pending review</div>
                  <div style={{ color: "#92400E", fontSize: 21, fontWeight: 500 }}>{pendingCount}</div>
                </div>
              )}
            </div>
            {rows.slice(0, 3).map((r) => (
              <div
                key={r.subscription_id}
                className="d-flex justify-content-between align-items-center py-2"
                style={{ borderTop: "1px solid #E5E7EB", fontSize: 13 }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{r.business_name || "-"}</div>
                  <div style={{ color: "#7C8495", fontSize: 12 }}>{r.loan_reference_number || "-"}</div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <span style={{ fontWeight: 500 }}>{formatMoney(r.invested_amount, r.currency)}</span>
                  <FundNowStateBadge state={r.state} />
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    );
  }

  // Withdrawal dialog; portalled so it can be opened from rows that live inside another table.
  const withdrawalModal = selected
    ? createPortal(
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(13,29,84,0.45)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 440, overflow: "hidden" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "16px 20px",
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 15 }}>Request withdrawal</span>
              <span
                style={{ cursor: submitting ? "not-allowed" : "pointer", fontSize: 20, color: "#7C8495" }}
                onClick={closeWithdrawal}
              >
                &times;
              </span>
            </div>
            <div style={{ padding: "16px 20px" }}>
              <div className="d-flex gap-4 mb-3">
                <div>
                  <div style={labelStyle}>Loan ref no.</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{selected.loan_reference_number || "-"}</div>
                </div>
                <div>
                  <div style={labelStyle}>Business</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{selected.business_name || "-"}</div>
                </div>
                <div>
                  <div style={labelStyle}>Amount</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {formatMoney(selected.invested_amount, selected.currency)}
                  </div>
                </div>
              </div>

              {checking ? (
                <Skeleton height={44} />
              ) : eligibility && !eligibility.eligible ? (
                <div
                  style={{
                    background: "#FEF2F2",
                    border: "1px solid #F6C9C9",
                    borderRadius: 10,
                    padding: "12px 14px",
                    fontSize: 12.5,
                  }}
                >
                  <div style={{ color: "#ED5151", fontWeight: 700, marginBottom: 4 }}>Withdrawal not available</div>
                  <ul className="mb-0 ps-3" style={{ color: "#7C8495" }}>
                    {(eligibility.reasons || []).map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                </div>
              ) : !eligibility ? (
                <div style={{ fontSize: 12.5, color: "#7C8495" }}>
                  We couldn't confirm eligibility right now. It will be checked again when you submit.
                </div>
              ) : (
                <div
                  style={{
                    background: "#ECFDF5",
                    border: "1px solid #B7EBD3",
                    borderRadius: 10,
                    padding: "12px 14px",
                    fontSize: 12.5,
                  }}
                >
                  <div style={{ color: "#059669", fontWeight: 700 }}>
                    Withdrawal window open
                    {timeLeft(eligibility?.window_deadline || selected.withdrawal_window_deadline, now)
                      ? ` · ${timeLeft(eligibility?.window_deadline || selected.withdrawal_window_deadline, now)}`
                      : ""}
                  </div>
                  <div style={{ color: "#7C8495", marginTop: 2 }}>
                    You can withdraw within {WITHDRAWAL_WINDOW_DAYS} days of investing, until the loan is disbursed.
                    BenneFin will review your request; once approved the amount returns to your available balance.
                  </div>
                </div>
              )}

              <div style={{ fontSize: 12, color: "#7C8495", margin: "14px 0 6px" }}>Reason (optional)</div>
              <textarea
                rows={3}
                maxLength={500}
                placeholder="Tell us why you want to withdraw..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={submitting}
                style={{
                  width: "100%",
                  fontSize: 13,
                  padding: "8px 10px",
                  borderRadius: 8,
                  border: "1px solid #E5E7EB",
                  resize: "vertical",
                  outline: "none",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
                padding: "14px 20px",
                borderTop: "1px solid #E5E7EB",
              }}
            >
              <button
                type="button"
                className="btn"
                onClick={closeWithdrawal}
                disabled={submitting}
                style={{ border: "1px solid #E5E7EB", background: "#fff", color: "#7C8495", fontSize: 13 }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn blue_btn"
                onClick={submitWithdrawal}
                disabled={submitting || checking || isAuditor || (eligibility && !eligibility.eligible)}
                style={{ fontSize: 13 }}
              >
                {submitting ? "Submitting..." : "Submit request"}
              </button>
            </div>
          </div>
        </div>
        ,
        document.body
      )
    : null;

  // Second line in the investor's own currency (e.g. $ for a USD wallet in a BHD loan), as the
  // legacy My Investments rows show it.
  const amountCell = (amount, invAmount, r) => (
    <>
      <div style={{ whiteSpace: "nowrap" }}>{formatMoney(amount, r.currency)}</div>
      {r.investor_currency && r.investor_currency !== r.currency && invAmount !== undefined && invAmount !== null && (
        <div style={{ fontSize: 15, color: "#555", marginTop: 2, whiteSpace: "nowrap" }}>
          {formatMoney(invAmount, r.investor_currency)}
        </div>
      )}
    </>
  );

  // QA #25: an investor with several investments in one loan also sees the loan-level total.
  const totalsByLoan = rows.filter((r) => r.state !== "withdrawn").reduce((acc, r) => {
    const k = String(r.investor_request_id);
    acc[k] = acc[k] || { count: 0, invested: 0, profit: 0, currency: r.currency };
    acc[k].count += 1;
    acc[k].invested += Number(r.invested_amount || 0);
    acc[k].profit += Number(r.profit_received || 0);
    return acc;
  }, {});

  // One Fund Now investment in the seven-column My Investments layout (TableData `columns` there).
  const rowNode = (r) => {
    const loanTotal = r.state !== "withdrawn" ? totalsByLoan[String(r.investor_request_id)] : null;
    const invested = Number(r.invested_amount || 0);
    const principalPct = invested > 0 ? Math.round((Number(r.principal_received || 0) / invested) * 10000) / 100 : 0;
    return (
      <tr key={r.subscription_id}>
        <td>
          <div>{r.loan_reference_number || "-"}</div>
          <div className="d-flex align-items-center gap-2 mt-1" style={{ fontSize: 11.5, color: "#7C8495" }}>
            <FundNowStateBadge state={r.state} />
            {r.state !== "withdrawn" && <span>{Number(r.share_percent || 0)}% share</span>}
          </div>
          {loanTotal && loanTotal.count > 1 && (
            <div style={{ fontSize: 11.5, color: "#7C8495", marginTop: 2, whiteSpace: "nowrap" }}>
              {loanTotal.count} investments in this loan: {formatMoney(loanTotal.invested, loanTotal.currency)} total,{" "}
              {formatMoney(loanTotal.profit, loanTotal.currency)} profit received
            </div>
          )}
        </td>
        <td style={{ minWidth: "9rem" }}>{amountCell(r.invested_amount, r.invested_amount_investor_currency, r)}</td>
        <td>{r.state === "withdrawn" ? "-" : principalPct === 0 ? 0 : principalPct + "%"}</td>
        <td>{r.investor_roi === undefined || r.investor_roi === null || r.investor_roi === "" ? "-" : r.investor_roi + "%"}</td>
        <td style={{ minWidth: "9rem" }}>
          {amountCell(r.profit_received, r.profit_received_investor_currency, r)}
          {r.state !== "closed" && r.state !== "withdrawn" && (
            <div style={{ fontSize: 11, color: "#7C8495" }}>received so far</div>
          )}
        </td>
        <td>{r.subscribed_date_time ? moment(r.subscribed_date_time).format("D MMM YYYY") : "-"}</td>
        <td>{renderWithdrawalCell(r)}</td>
      </tr>
    );
  };

  if (variant === "rows") {
    return (
      <>
        {rows.map(rowNode)}
        {withdrawalModal}
      </>
    );
  }

  // "items": hand the rows to the caller as sortable items (values keyed like the legacy My
  // Investments columns) so they can be merged and sorted with legacy rows in one table.
  if (variant === "items") {
    const items = rows.map((r) => ({
      key: "fn-" + r.subscription_id,
      values: {
        loan_reference_number: r.loan_reference_number || "",
        final_loan_amount: Number(r.invested_amount || 0),
        principal_repaid_percentage:
          Number(r.invested_amount) > 0 ? Number(r.principal_received || 0) / Number(r.invested_amount) * 100 : 0,
        investor_roi: Number(r.investor_roi || 0),
        net_returns: Number(r.profit_received || 0),
        created_date_time: Number(r.subscribed_date_time || 0),
      },
      node: rowNode(r),
    }));
    return children ? children(items, withdrawalModal, loading) : null;
  }

  const headers = ["Loan Ref No.", "Business", "Invested", "Share", "Status", "Received", "Profit Received", "Withdrawal"];

  return (
    <div>
      <Table striped className="my_loan_table">
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h}>{loading ? <Skeleton width={80} height={15} /> : h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            [0, 1].map((i) => (
              <tr key={i}>
                {headers.map((h) => (
                  <td key={h}>
                    <Skeleton width={80} height={15} />
                  </td>
                ))}
              </tr>
            ))
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={headers.length} className="text-center" style={{ color: "#7C8495" }}>
                No Fund Now investments yet.
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.subscription_id}>
                <td>{r.loan_reference_number || "-"}</td>
                <td>{r.business_name || "-"}</td>
                <td style={{ fontWeight: 500 }}>{formatMoney(r.invested_amount, r.currency)}</td>
                <td>{r.state === "withdrawn" ? "—" : `${Number(r.share_percent || 0)}%`}</td>
                <td>
                  <FundNowStateBadge state={r.state} />
                </td>
                <td>{formatMoney(r.received_total, r.currency)}</td>
                <td>{formatMoney(r.profit_received, r.currency)}</td>
                <td>{renderWithdrawalCell(r)}</td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {withdrawalModal}
    </div>
  );
};

export default FundNowInvestments;
